import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoSlide } from '../../../../hooks/slider';
import ModelViewer from '../model-viewer/ModelViewer';

export interface ImageSlide { type?: 'image'; url: string; caption: string; }
export interface ViewerSlide { type: 'viewer'; src: string; caption: string; }
export type Slide = ImageSlide | ViewerSlide;

const CubeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M2 7L12 12M12 12L22 7M12 12V22" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useAutoSlide(slides.length);
  const current = slides[currentSlide];

  return (
    <div className="flex flex-col-reverse md:flex-row justify-center lg:items-start gap-[16px] md:gap-[24px]">
      <div className="relative w-full xl:w-[448px] lg:ml-[48px] overflow-hidden flex flex-col items-center">
        <div className="xl:w-full w-[345px] h-[382px] rounded-[22px] overflow-hidden">
          {current.type === 'viewer' ? (
            <ModelViewer src={(current as ViewerSlide).src} />
          ) : (
            <img
              src={(current as ImageSlide).url}
              alt={current.caption}
              className="w-full h-full object-cover transition-all duration-300"
            />
          )}
        </div>

        {slides.length > 1 && (
          <div className="flex gap-[24px] mt-[16px] w-full">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-[4px] rounded-[32px] transition-all duration-200 ${
                  slides.length <= 4 ? 'w-[68px] xl:w-[94px]' : 'flex-1'
                } ${currentSlide === index ? 'bg-base-black' : 'bg-blue-500'}`}
                aria-label={t('library.slider.goToSlide', { n: index + 1 })}
              />
            ))}
          </div>
        )}
      </div>

      <div className="hidden xl:flex md:flex-col gap-[16px] overflow-auto max-w-full md:max-h-[382px]">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-[64px] h-[70px] rounded-[12px] overflow-hidden transition-all duration-200 border ${
              currentSlide === index ? 'border-blue-500' : 'border-transparent'
            }`}
          >
            {slide.type === 'viewer' ? (
              <div className="w-full h-full flex items-center justify-center bg-surface-secondary text-content-secondary">
                <CubeIcon />
              </div>
            ) : (
              <img
                src={(slide as ImageSlide).url}
                alt={slide.caption}
                className="w-full h-full object-cover"
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Slideshow;
